import { Shield, AlertTriangle, AlertCircle, Info, CheckCircle } from 'lucide-react'
import type { AnalysisResult, SeverityLevel } from '../types'

interface ScoreDisplayProps {
  result: AnalysisResult
}

const severityConfig = {
  CRITICAL: {
    icon: AlertCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-200',
    description: 'Immediate action required'
  },
  HIGH: {
    icon: AlertTriangle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-200',
    description: 'High priority security issues'
  },
  MEDIUM: {
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    borderColor: 'border-yellow-200',
    description: 'Medium priority concerns'
  },
  LOW: {
    icon: Info,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-200',
    description: 'Low priority findings'
  },
  NONE: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
    description: 'No threats detected'
  }
}

export default function ScoreDisplay({ result }: ScoreDisplayProps) {
  const config = severityConfig[result.overall_score.level]
  const IconComponent = config.icon

  const getScoreColor = (score: number, level: SeverityLevel) => {
    if (level === 'NONE') return 'text-green-600'
    if (score >= 80) return 'text-red-600'
    if (score >= 60) return 'text-orange-600'
    if (score >= 30) return 'text-yellow-600'
    return 'text-orange-600'
  }

  const getProgressBarColor = (score: number, level: SeverityLevel) => {
    if (level === 'NONE') return 'bg-green-500'
    if (score >= 80) return 'bg-red-500'
    if (score >= 60) return 'bg-orange-500'
    if (score >= 30) return 'bg-yellow-500'
    return 'bg-orange-500'
  }

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='50' cy='50' r='4'/%3E%3Ccircle cx='10' cy='10' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      <div className="relative p-8">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-xl ${config.bgColor} ${config.borderColor} border-2`}>
              <IconComponent className={`h-8 w-8 ${config.color}`} />
            </div>
            <div>
              <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                Severity Score
              </h4>
              <p className={`text-sm font-semibold ${config.color} flex items-center space-x-2`}>
                <span className={`inline-block w-2 h-2 rounded-full ${getProgressBarColor(result.overall_score.score, result.overall_score.level)}`}></span>
                <span>{result.overall_score.level} - {config.description}</span>
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="relative">
              <div className={`text-5xl font-black ${getScoreColor(result.overall_score.score, result.overall_score.level)} drop-shadow-sm`}>
                {result.overall_score.score}
              </div>
              <div className="text-lg font-medium text-gray-400 dark:text-gray-500 -mt-1">
                /100
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-medium">
              {result.threats_detected} {result.threats_detected === 1 ? 'threat' : 'threats'} detected
            </div>
            {result.ioc_matches && result.ioc_matches.length > 0 && (
              <div className="text-sm text-red-600 dark:text-red-400 font-medium">
                {result.ioc_matches.length} malicious {result.ioc_matches.length === 1 ? 'indicator' : 'indicators'}
              </div>
            )}
          </div>
        </div>

        {/* Progress Section */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Threat Level Assessment</span>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${config.bgColor} ${config.color} border ${config.borderColor}`}>
              {result.overall_score.level}
            </span>
          </div>
          
          <div className="relative">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 shadow-inner">
              <div
                className={`h-4 rounded-full transition-all duration-1000 ease-out shadow-md ${getProgressBarColor(result.overall_score.score, result.overall_score.level)}`}
                style={{ 
                  width: `${Math.max(result.overall_score.score, 3)}%`,
                  background: `linear-gradient(90deg, ${
                    result.overall_score.level === 'NONE' ? '#10b981, #34d399' :
                    result.overall_score.score >= 80 ? '#dc2626, #f87171' :
                    result.overall_score.score >= 60 ? '#ea580c, #fb923c' :
                    result.overall_score.score >= 30 ? '#d97706, #fbbf24' :
                    '#ea580c, #fb923c'
                  })`
                }}
              />
            </div>
            {/* Score indicator */}
            <div 
              className="absolute -top-8 transform -translate-x-1/2 transition-all duration-1000 ease-out"
              style={{ left: `${Math.max(result.overall_score.score, 3)}%` }}
            >
              <div className={`px-2 py-1 rounded text-xs font-bold text-white ${getProgressBarColor(result.overall_score.score, result.overall_score.level)} shadow-lg`}>
                {result.overall_score.score}
              </div>
            </div>
          </div>
          
          <div className="flex justify-between text-xs font-medium text-gray-500 dark:text-gray-400 mt-3">
            <span className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span>None</span>
            </span>
            <span className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span>Low</span>
            </span>
            <span className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
              <span>Medium</span>
            </span>
            <span className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span>High</span>
            </span>
            <span className="flex items-center space-x-1">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span>Critical</span>
            </span>
          </div>
        </div>

        {result.overall_score.score === 0 && (
          <div className="mt-6 flex items-center space-x-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex-shrink-0">
              <Shield className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-sm font-medium text-green-800 dark:text-green-300">
              Excellent! No security threats detected in your logs.
            </span>
          </div>
        )}
      </div>
    </div>
  )
}